import type { SettingsNavItem } from '../components/app-shell/AppShell';
import {
  resolveEntryPath,
  SETTINGS_MODULE,
  SHELL_MODULES,
  type ModuleId,
  type ShellModule,
  type ShellPersona,
} from './moduleRegistry';

export type StaffShellNavId = ModuleId;
export type StaffSidebarPersona = Exclude<ShellPersona, 'parent' | 'guest'>;

export type StaffSidebarNavDescriptor = {
  id: StaffShellNavId;
  label: string;
  icon: SettingsNavItem['icon'];
  route: string;
  entryPath: string;
};

export type StaffSidebarNavigationState = {
  activeNavId: StaffShellNavId | 'settings';
  navItems: SettingsNavItem[];
  settingsItem: SettingsNavItem | undefined;
  routeMap: Map<StaffShellNavId | 'settings', string>;
  activeDescriptor: StaffSidebarNavDescriptor | undefined;
  descriptors: StaffSidebarNavDescriptor[];
};

export type StaffSidebarNavigationOptions = {
  persona: ShellPersona;
  activePath?: string;
  enabledNavIds?: Set<string> | string[] | null;
  includeSettings?: boolean;
};

const normalizePath = (path: string): string =>
  (path || '').replace(/\/$/, '') || '/';

const toEnabledSet = (value?: Set<string> | string[] | null): Set<string> | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Set) {
    return value;
  }
  return new Set(value);
};

const filterDescriptorById = (value: StaffSidebarNavDescriptor, enabledSet: Set<string> | null): boolean => {
  if (!enabledSet) return true;
  return enabledSet.has(value.id);
};

const resolveStaffPersona = (persona: ShellPersona): StaffSidebarPersona | null => {
  if (persona === 'parent' || persona === 'guest') {
    return null;
  }
  return persona;
};

const toShellNavDescriptor = (
  module: ShellModule,
  persona: Exclude<ShellPersona, 'parent' | 'guest'>,
): StaffSidebarNavDescriptor | null => {
  const entryPath = resolveEntryPath(module, persona);
  if (!entryPath) {
    return null;
  }
  return {
    id: module.id,
    label: module.label,
    icon: module.icon,
    route: module.routeRoot,
    entryPath,
  };
};

const STAFF_DEFAULT_ACTIVE_ID: StaffShellNavId = 'admissions';

const isStaffShellDescriptor = (
  value: StaffSidebarNavDescriptor | null,
): value is StaffSidebarNavDescriptor => Boolean(value);

const resolvePersonaBlueprint = (
  persona: StaffSidebarPersona,
  includeSettings = true,
): StaffSidebarNavDescriptor[] => {
  const modules = SHELL_MODULES
    .filter((module) => module.allowedPersonas.includes(persona))
    .map((module) => toShellNavDescriptor(module, persona))
    .filter(isStaffShellDescriptor);

  if (!includeSettings || !SETTINGS_MODULE.allowedPersonas.includes(persona)) {
    return modules;
  }

  const settingsDescriptor = toShellNavDescriptor(SETTINGS_MODULE, persona);
  return settingsDescriptor ? [...modules, settingsDescriptor] : modules;
};

export const resolveStaffSidebarBlueprint = (
  persona: ShellPersona,
  includeSettings = true,
): StaffSidebarNavDescriptor[] => {
  const effectivePersona = resolveStaffPersona(persona);
  if (!effectivePersona) {
    return [];
  }

  return resolvePersonaBlueprint(effectivePersona, includeSettings);
};

export const resolveStaffSidebarBlueprintForPersona = (
  persona: StaffSidebarPersona,
  includeSettings = true,
): StaffSidebarNavDescriptor[] => resolvePersonaBlueprint(persona, includeSettings);

export const resolveStaffSidebarActiveDescriptor = (
  descriptors: StaffSidebarNavDescriptor[],
  activePath: string,
): StaffSidebarNavDescriptor | undefined => {
  const normalizedActivePath = normalizePath(activePath);
  const isSettingsPath = normalizedActivePath === SETTINGS_MODULE.routeRoot
    || normalizedActivePath.startsWith(`${SETTINGS_MODULE.routeRoot}/`);
  if (isSettingsPath) {
    return descriptors.find((descriptor) => descriptor.id === 'settings');
  }

  const candidates = [...descriptors]
    .filter((descriptor) => descriptor.id !== 'settings')
    .sort((a, b) => b.route.length - a.route.length);
  return candidates.find((descriptor) =>
    normalizedActivePath === descriptor.route || normalizedActivePath.startsWith(`${descriptor.route}/`)
  );
};

export const resolveLiveFilteredStaffSidebarNavigation = (
  descriptors: StaffSidebarNavDescriptor[],
  enabledNavIds?: Set<string> | string[] | null,
): StaffSidebarNavDescriptor[] => {
  const enabledSet = toEnabledSet(enabledNavIds);
  if (!enabledSet) {
    return descriptors;
  }

  return descriptors.filter((descriptor) => filterDescriptorById(descriptor, enabledSet));
};

const resolveDefaultActiveDescriptor = (
  descriptors: StaffSidebarNavDescriptor[],
): StaffSidebarNavDescriptor | undefined =>
  descriptors.find((descriptor) => descriptor.id !== 'settings') ?? descriptors[0];

export const resolveStaffSidebarNavigationState = (
  options: StaffSidebarNavigationOptions,
): StaffSidebarNavigationState => {
  const {
    persona,
    activePath = '/',
    enabledNavIds,
    includeSettings = true,
  } = options;

  const baseBlueprint = resolveStaffSidebarBlueprint(persona, includeSettings);
  const descriptors = resolveLiveFilteredStaffSidebarNavigation(baseBlueprint, enabledNavIds);
  const activeDescriptor = resolveStaffSidebarActiveDescriptor(descriptors, activePath);

  const navItems = descriptors
    .filter((descriptor) => descriptor.id !== 'settings')
    .map((descriptor) => ({
      id: descriptor.id,
      label: descriptor.label,
      icon: descriptor.icon,
    }));

  const settingsDescriptor = descriptors.find((descriptor) => descriptor.id === 'settings');
  const settingsItem = settingsDescriptor
    ? {
        id: settingsDescriptor.id,
        label: settingsDescriptor.label,
        icon: settingsDescriptor.icon,
      }
    : undefined;

  const routeMap = new Map<StaffShellNavId | 'settings', string>(
    descriptors.map((descriptor) => [descriptor.id, descriptor.entryPath])
  );
  const activeNavId = activeDescriptor?.id
    ?? resolveDefaultActiveDescriptor(descriptors)?.id
    ?? settingsItem?.id
    ?? STAFF_DEFAULT_ACTIVE_ID;

  return {
    activeNavId,
    navItems,
    settingsItem,
    routeMap,
    activeDescriptor,
    descriptors,
  };
};
